import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import * as manufacturingApi from '../../../lib/api/manufacturing';

const WorkflowDialog = ({ open, onClose, onSave, selectedWorkflow }) => {
  const [loading, setLoading] = useState(false);
  const [processList, setProcessList] = useState([]); // 공정 마스터 목록
  const [formData, setFormData] = useState({
    workflowName: '',
    description: '',
    selectedProcesses: [], // 선택된 공정 UUID 배열
    processOrder: [], // 공정 순서 배열 { processUuid, processNm, isRequired }
  });
  // 설명을 우리가 만들어 넣고 있는 중인지.
  // 직접 쓴 설명을 공정 선택이 덮어쓰면 안 되므로 구분이 필요하다.
  const [descriptionIsAuto, setDescriptionIsAuto] = useState(true);

  // ===========================================
  // 공정 마스터 조회
  // ===========================================
  const fetchProcesses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await manufacturingApi.getActiveProcesses();
      console.log('공정 마스터 목록:', response.data);
      
      if (response.data.success) {
        const list = response.data.data || [];
        setProcessList(list);
      }
    } catch (error) {
      console.error('공정 마스터 조회 에러:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchProcesses();
    }
  }, [open, fetchProcesses]);

  // ===========================================
  // 폼 데이터 초기화
  // ===========================================
  useEffect(() => {
    if (open) {
      if (selectedWorkflow) {
        // 수정 또는 복사 모드
        const details = selectedWorkflow.details || [];
        const orderedProcesses = details
          .sort((a, b) => (a.processSeq || 0) - (b.processSeq || 0))
          .map(d => ({
            processUuid: d.processUuid,
            processNm: d.processNm,
            isRequired: d.isRequired === 'Y',
            qcTargetYn: d.qcTargetYn === 'Y',
            processSeq: d.processSeq,
          }));
        
        const saved = selectedWorkflow.description || '';
        setFormData({
          workflowName: selectedWorkflow.workflowName || '',
          description: saved,
          selectedProcesses: orderedProcesses.map(p => p.processUuid),
          processOrder: orderedProcesses,
        });
        // 저장된 설명이 지금 공정 순서 그대로면 우리가 만든 것이다. 계속 따라가게 둔다.
        setDescriptionIsAuto(saved === '' || saved === generateDescription(orderedProcesses));
      } else {
        // 신규 모드
        setFormData({
          workflowName: '',
          description: '',
          selectedProcesses: [],
          processOrder: [],
        });
        setDescriptionIsAuto(true);
      }
    }
  }, [selectedWorkflow, open]);

  // ===========================================
  // 핸들러
  // ===========================================
  const handleChange = (field, value) => {
    if (field === 'description') {
      // 직접 쓰기 시작하면 더는 건드리지 않는다. 다시 비우면 자동 생성으로 돌아온다.
      setDescriptionIsAuto(value.trim() === '');
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // 공정 순서로 설명 자동 생성
  const generateDescription = (processOrder) => {
    return processOrder.map(p => p.processNm).join(' \u2192 ');
  };

  // 이 설명을 우리가 만들어 넣고 있는 중인지.
  //
  // 예전에는 값의 생김새로 짐작했다. 비었거나 화살표가 있으면 자동값으로 봤다.
  // 그런데 공정을 하나만 고른 설명("교육")에는 화살표가 없다. 그래서 두 번째 공정부터
  // 사람이 직접 쓴 설명으로 오해하고 갱신을 멈췄다. 플래그로 명시한다.
  const isAutoDescription = () => descriptionIsAuto;

  const handleProcessToggle = (process) => {
    const processUuid = process.processUuid;
    const isSelected = formData.selectedProcesses.includes(processUuid);

    if (isSelected) {
      // 제거
      setFormData((prev) => {
        const newOrder = prev.processOrder.filter(p => p.processUuid !== processUuid);
        return {
          ...prev,
          selectedProcesses: prev.selectedProcesses.filter(id => id !== processUuid),
          processOrder: newOrder,
          description: isAutoDescription()
            ? generateDescription(newOrder)
            : prev.description,
        };
      });
    } else {
      // 추가
      const newProcess = {
        processUuid: process.processUuid,
        processNm: process.processNm,
        isRequired: process.isRequired === 'Y',
        qcTargetYn: false, // 검사 대상 여부 기본값. 사용자가 순서 목록에서 직접 지정한다.
      };
      setFormData((prev) => {
        const newOrder = [...prev.processOrder, newProcess];
        return {
          ...prev,
          selectedProcesses: [...prev.selectedProcesses, processUuid],
          processOrder: newOrder,
          description: isAutoDescription()
            ? generateDescription(newOrder)
            : prev.description,
        };
      });
    }
  };

  const moveProcess = (index, direction) => {
    const newOrder = [...formData.processOrder];
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= newOrder.length) return;

    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];

    setFormData((prev) => ({
      ...prev,
      processOrder: newOrder,
      description: isAutoDescription()
        ? generateDescription(newOrder)
        : prev.description,
    }));
  };

  // 공정검사 대상 여부 토글. 해당 순서 항목만 갱신한다.
  const toggleQcTarget = (index, checked) => {
    setFormData((prev) => {
      const newOrder = [...prev.processOrder];
      newOrder[index] = { ...newOrder[index], qcTargetYn: checked };
      return {
        ...prev,
        processOrder: newOrder,
      };
    });
  };

  // 완품검사(INSPECTION) 공정은 검사 대상 지정 체크박스를 띄우지 않는다.
  // 완품 QC는 judgeFinalQc 판정으로만 완료되는 별개의 흐름이라 대상 여부를 지정할 필요가 없다.
  const isInspectionProcess = (processUuid) => {
    const master = processList.find((p) => p.processUuid === processUuid);
    return master?.processTyp === 'INSPECTION';
  };

  const handleSave = () => {
    if (!formData.workflowName.trim()) {
      alert('워크플로우명을 입력하세요.');
      return;
    }

    if (formData.processOrder.length === 0) {
      alert('최소 1개 이상의 공정을 선택하세요.');
      return;
    }

    // 공정 흐름 설명 자동 생성
    const autoDescription = formData.processOrder.map(p => p.processNm).join(' → ');

    // API 요청 형식으로 변환
    const details = formData.processOrder.map((p, idx) => ({
      processUuid: p.processUuid,
      processNm: p.processNm,
      processSeq: idx + 1,
      isRequired: p.isRequired ? 'Y' : 'N',
      qcTargetYn: p.qcTargetYn ? 'Y' : 'N',
    }));

    onSave({
      workflowName: formData.workflowName,
      description: formData.description || autoDescription,
      details: details,
    });
  };

  const handleClose = () => {
    setFormData({
      workflowName: '',
      description: '',
      selectedProcesses: [],
      processOrder: [],
    });
    setDescriptionIsAuto(true);
    onClose();
  };

  const stepCount = formData.processOrder.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">
              {selectedWorkflow?.workflowUuid ? '워크플로우 수정' : '워크플로우 추가'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2">
                  생산 계획 작성 시 선택할 수 있는 공정 워크플로우를 등록합니다.
                  <br />
                  공정 마스터에서 공정을 선택하고 순서를 조정할 수 있습니다.
                </Typography>
              </Alert>
            </Box>

            {/* 기본 정보 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                기본 정보
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="워크플로우명 *"
                    value={formData.workflowName}
                    onChange={(e) => handleChange('workflowName', e.target.value)}
                    placeholder="예: 표준 공정, 간단 공정"
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="설명 (선택)"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="공정 흐름이 자동으로 표시됩니다"
                    helperText="비워두면 선택한 공정 단계로 자동 생성됩니다"
                  />
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={3}>
              {/* 왼쪽: 공정 선택 */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      공정 선택
                    </Typography>
                    <Chip label={`선택 ${stepCount}개`} size="small" color="primary" />
                  </Box>

                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa', maxHeight: 400, overflow: 'auto' }}>
                    {processList.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                        등록된 공정이 없습니다.
                        <br />
                        공정 마스터에서 공정을 먼저 등록해주세요.
                      </Typography>
                    ) : (
                      processList.map((process) => {
                        const isSelected = formData.selectedProcesses.includes(process.processUuid);
                        const isRequired = process.isRequired === 'Y';
                        return (
                          <Box
                            key={process.processUuid}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 1.5,
                              mb: 1,
                              backgroundColor: isSelected ? '#e3f2fd' : 'white',
                              border: isSelected ? '2px solid #1976D2' : '1px solid #ddd',
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5' },
                              '&:last-child': { mb: 0 },
                            }}
                            onClick={() => handleProcessToggle(process)}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleProcessToggle(process)}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" fontWeight={isRequired ? 600 : 400}>
                                    {process.processNm}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {process.processCd}
                                  </Typography>
                                </Box>
                              }
                              sx={{ flex: 1, m: 0 }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            {isRequired && (
                              <Chip label="필수" size="small" color="primary" variant="outlined" />
                            )}
                          </Box>
                        );
                      })
                    )}
                  </Paper>
                </Box>
              </Grid>

              {/* 오른쪽: 선택된 공정 순서 */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    선택된 공정 순서
                  </Typography>

                  {stepCount > 0 ? (
                    <Paper variant="outlined" sx={{ backgroundColor: '#f5f5f5', maxHeight: 400, overflow: 'auto' }}>
                      <List sx={{ p: 1 }}>
                        {formData.processOrder.map((process, index) => {
                          const isFirst = index === 0;
                          const isLast = index === formData.processOrder.length - 1;

                          return (
                            <ListItem
                              key={process.processUuid}
                              sx={{
                                backgroundColor: 'white',
                                mb: 1,
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                                '&:last-child': { mb: 0 },
                              }}
                            >
                              <DragIndicatorIcon
                                sx={{ color: '#9e9e9e', mr: 1, fontSize: 20 }}
                              />
                              <Chip
                                label={index + 1}
                                size="small"
                                color="primary"
                                sx={{ mr: 1, minWidth: 32 }}
                              />
                              <ListItemText
                                primary={process.processNm}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: process.isRequired ? 600 : 400,
                                }}
                              />
                              {!isInspectionProcess(process.processUuid) && (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={!!process.qcTargetYn}
                                      onChange={(e) => toggleQcTarget(index, e.target.checked)}
                                    />
                                  }
                                  label="공정검사"
                                  sx={{ mr: 1 }}
                                />
                              )}
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => moveProcess(index, -1)}
                                  disabled={isFirst}
                                  sx={{ mr: 0.5 }}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => moveProcess(index, 1)}
                                  disabled={isLast}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        공정을 선택하면 순서가 여기에 표시됩니다
                      </Typography>
                    </Paper>
                  )}

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    💡 화살표 버튼으로 공정의 순서를 변경할 수 있습니다.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* 미리보기 */}
            {stepCount > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  공정 흐름 미리보기
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #2196F3',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#1565C0' }}>
                    {formData.processOrder.map(p => p.processNm).join(' → ')}
                  </Typography>
                </Paper>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit">
          취소
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={!formData.workflowName.trim() || formData.processOrder.length === 0}
        >
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowDialog;
